import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CoreService } from '../../../../services/core.service';
import { SectionGridComponent } from '../../components/section-grid/section-grid.component';
import { FooterGridComponent } from '../../components/footer-grid/footer-grid.component';

@Component({
  selector: 'app-edit-user',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SectionGridComponent,
    FooterGridComponent
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss'
})
export class EditUserComponent implements OnInit {
  @Input() userID!: number;
  @Input() embeddedMode = false;
  @Input() hasBorder = true;
  @Input() companyID!: number;

  @Output() userUpdated = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  editForm: FormGroup;
  isSubmitting = false;
  isLoading = true;
  successMessage = '';
  errorMessage = '';

  roles = [
    { value: 1, label: 'Administrator' },
    { value: 2, label: 'Content Creator' },
    { value: 4, label: 'Content Editor' }
  ];

  statuses = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
  ];

  constructor(
    private fb: FormBuilder,
    private coreService: CoreService,
    private route: ActivatedRoute
  ) {
    this.editForm = this.createForm();
  }

  ngOnInit(): void {
    if (!this.userID) {
      const idParam = this.route.snapshot.paramMap.get('user');
      if (idParam) {
        this.userID = Number(idParam);
      }
    }

    if (!this.companyID) {
      this.companyID = 1; // test/default
    }

    this.loadUserData();
  }

  createForm(): FormGroup {
    return this.fb.group({
      userEmail: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      userGroupID: ['', Validators.required],
      isActive: ['', Validators.required]
    });
  }

  loadUserData(): void {
    if (!this.userID) {
      this.errorMessage = 'User ID is required';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this.coreService
      .getData(`/manageAccount/user/edit/${this.userID}`, { companyID: this.companyID })
      .subscribe({
        next: (response: any) => {
          const userData = response.data || response;
          console.log(userData);
          this.editForm.patchValue({
            userEmail: userData.userEmail || '',
            firstName: userData.firstName || userData.name?.split(' ')[0] || '',
            lastName: userData.lastName || userData.name?.split(' ')[1] || '',
            phoneNumber: userData.phoneNumber || userData.Phone || '',
            userGroupID: userData.userGroupID || '',
            isActive: userData.isActive === 0 ? 0 : (userData.isActive || null)
          });

          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading user data:', error);
          this.errorMessage = error.error?.message || 'Failed to load user data. Please try again.';
          this.isLoading = false;
        }
      });
  }

  onSubmit(): void {
    if (this.editForm.invalid) {
      this.markFormGroupTouched(this.editForm);
      return;
    }

    this.isSubmitting = true;
    this.successMessage = '';
    this.errorMessage = '';

    const formData = {
      ...this.editForm.value,
      id: this.userID,
      companyID: this.companyID,
      name: `${this.editForm.value.firstName} ${this.editForm.value.lastName}`.trim()
    };

    this.coreService.postData(`manageAccount/user/update/${this.userID}`, formData)
      .subscribe({
        next: (response: any) => {
          this.successMessage = 'User updated successfully!';
          this.userUpdated.emit(response.data || formData);

          setTimeout(() => {
            this.editForm.markAsPristine();
            this.editForm.markAsUntouched();
          }, 1000);
        },
        error: (error) => {
          console.error('Update error:', error);
          this.errorMessage = error.error?.message || 'Failed to update user. Please try again.';
        },
        complete: () => {
          this.isSubmitting = false;
        }
      });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getControl(controlName: string) {
    return this.editForm.get(controlName);
  }
}
